import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, FormBuilder, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { Router } from '@angular/router';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { HttpStatusCode } from 'axios';
import { AgentOnboardingService } from './agent-onboarding-service/agent-onboarding.service';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { AgentOnboarding } from './agent-onboarding-interfaces/agent-onboarding.interface';
import { ApplicantNavigationPage } from '../applicant-navigation/applicant-navigation.page';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { OnboardingAuditLogDialog } from './onboarding-audit-log-dialog/onboarding-audit-log.dialog';
import { OnboardingAuditLogDialogData } from './agent-onboarding-interfaces/onboarding-audit-log.interface';


@Component({
  selector: 'app-agent-onboarding',
  imports: [
    CommonModule,
    PiasLayoutComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTableModule,
    MatChipsModule,
    MatInputModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatMenuModule,
    MatDividerModule,
    MatBadgeModule,
    ApplicantNavigationPage,
    MatSelectModule,
    FormsModule,
    MatDialogModule

  ],
  templateUrl: './agent-onboarding.page.html',
  styleUrl: './agent-onboarding.page.scss'
})
export class AgentOnboardingPage implements OnInit {
  private router = inject(Router);
  private formBuilder = inject(FormBuilder);
  private sessionStorageService = inject(SessionStorageService);
  private agentOnboardingService = inject(AgentOnboardingService);
  private snackBarService = inject(SnackbarService);
  private matDialog = inject(MatDialog);
  agentOnboardingTableFilterFc = new FormControl("");
  searchQueryFg!: FormGroup;
  setSprinner = signal(false);
  protected currentUserRole: string = "";

  displayedColumns: string[] = ['fullNameNric', 'designation', 'branch', 'requesterName', 'dateOfApplication', 'status', 'actions'];

  allApplicants: AgentOnboarding[] = [];

  filteredApplicants: AgentOnboarding[] = [];
  pagedApplicants: AgentOnboarding[] = [];
  selectedStatus = '';

  pageSize = 5;
  pageIndex = 0;

  get stats() {
    return {
      DraftCreated: this.allApplicants.filter(a => a.status.userStatus === 'Draft Created').length,
      CandidateSubmission: this.allApplicants.filter(a => a.status.userStatus === 'Candidate Submission').length,
      ManagerReview: this.allApplicants.filter(a => a.status.userStatus === 'Manager Review').length,
      OnboardingProcessing: this.allApplicants.filter(a => a.status.userStatus === 'Onboarding Processing').length,
      BDMInterview: this.allApplicants.filter(a => a.status.userStatus === 'BDM Interview').length,
      Rectification: this.allApplicants.filter(a => ['Rectification', 'Candidate Update (Due Diligence)', 'Candidate Update (Assessment)'].includes(a.status.userStatus)).length,
      DSUInitialAssessment: this.allApplicants.filter(a => a.status.userStatus === 'DSU Initial Assessment').length,
      DSUFinalAssessment: this.allApplicants.filter(a => a.status.userStatus === 'DSU Final Assessment').length,
      DSUManagerApproval: this.allApplicants.filter(a => a.status.userStatus === 'DSU Manager Approval').length,
      OnboardingCompleted: this.allApplicants.filter(a => a.status.userStatus === 'Onboarding Completed').length,
    };
  }

  async ngOnInit(): Promise<void> {
    this.searchQueryFg = this.formBuilder.group({
      searchApplicants: [''],
      // drCategory: ['']
    });
    this.currentUserRole = this.sessionStorageService.getCurrentUserRole();
    if (['NEWREP', 'Introducer - Individual', 'Introducer - Corporate'].includes(this.currentUserRole)) {

    } else {
      await this.getAgentOnboardList();
    }

    const filterString = sessionStorage.getItem('filter1');
    const filter = filterString ? JSON.parse(filterString) : null;

    if (filter) {
      this.selectedStatus = filter.selectedStatus;
      await this.__applyAgentonboardFilter();
    }

  }

  async getAgentOnboardList() {
    this.setSprinner.set(true);
    const responseData = await this.agentOnboardingService
      .getAgentOnboardList()
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      if (this.currentUserRole === 'Onboarding') {
        this.allApplicants = responseData.data.responsedata.filter((a: { status: { userStatus: string; }; }) => a.status.userStatus !== 'Draft Created');
      } else {
        this.allApplicants = responseData.data.responsedata;
      }

      this.filteredApplicants = [...this.allApplicants];
      this.updatePagedData();

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  protected clickFilters(status: string) {
    this.selectedStatus = status;
    this.__applyAgentonboardFilter();
  }

  onStatusFilterChange() {
    this.__applyAgentonboardFilter();
  }

  __applyAgentonboardFilter(): void {
    this.filteredApplicants = this.allApplicants;
    if (this.selectedStatus) {
      let Status = this.selectedStatus == 'Rectification' ? ['Rectification', 'Candidate Update (Due Diligence)', 'Candidate Update (Assessment)'] : this.selectedStatus;
      this.filteredApplicants = this.filteredApplicants.filter(applicant =>
        Status.includes(applicant.status.userStatus)
      );
    }
    if (this.searchQueryFg.get('searchApplicants')?.value.trim()) {
      const q = this.searchQueryFg.get('searchApplicants')?.value.toString().toLowerCase();
      this.filteredApplicants = this.filteredApplicants.filter(
        a =>
          a.fullNameNric?.toLowerCase().includes(q) ||
          a.companyName?.toLowerCase().includes(q) ||
          a.requesterName?.toLowerCase().includes(q) ||
          a.branch?.toLowerCase().includes(q) ||
          a.designation?.toLowerCase().includes(q) ||
          a.dateOfApplication?.toLowerCase().includes(q) ||
          a.status.userStatus?.toLowerCase().includes(q)
      );
    }
    this.pageIndex = 0;
    this.updatePagedData();
    const filter = {
      selectedStatus: this.selectedStatus,
    };
    sessionStorage.setItem('filter1', JSON.stringify(filter));
  }
  clearSearch() {
    this.searchQueryFg.get('searchApplicants')?.setValue('');
    this.__applyAgentonboardFilter();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagedData();
  }

  updatePagedData(): void {
    const start = this.pageIndex * this.pageSize;
    this.pagedApplicants = this.filteredApplicants.slice(start, start + this.pageSize);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Draft Created': return 'status-draft';
      case 'Candidate Submission': return 'status-submission';
      case 'Manager Review': return 'status-review';
      case 'Onboarding Processing': return 'status-processing';
      case 'Candidate Update (Due Diligence)': return 'status-rect';
      case 'Candidate Update (Assessment)': return 'status-rect';
      case 'Rectification': return 'status-rect';
      case 'DSU Initial Assessment': return 'status-initial';
      case 'BDM Interview': return 'status-interview';
      case 'DSU Final Assessment': return 'status-final';
      case 'DSU Manager Approval': return 'status-approval';
      case 'Onboarding Completed': return 'status-completed';
      default: return '';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'Draft Created': return 'edit_document';
      case 'Candidate Submission': return 'schedule';
      case 'Manager Review': return 'rate_review';
      case 'Onboarding Processing': return 'autorenew';
      case 'Candidate Update (Due Diligence)': return 'edit';
      case 'Candidate Update (Assessment)': return 'edit';
      case 'Rectification': return 'edit';
      case 'DSU Initial Assessment': return 'assignment';
      case 'BDM Interview': return 'forum';
      case 'DSU Final Assessment': return 'assignment_turned_in';
      case 'DSU Manager Approval': return 'pending_actions';
      case 'Onboarding Completed': return 'task_alt';
      default: return 'help';
    }
  }
  getEndIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredApplicants.length);
  }


  viewApplicant(applicant: AgentOnboarding): void {
    // alert(`Viewing: ${applicant.name} — ${applicant.position}`);
    if (applicant.status.userStatus == 'Draft Created') {
      this.router.navigate(['agent-onboarding/view-onboarding-application'], {
        state: {
          id: applicant.id,
          status: applicant.status.userStatus
        }
      });

    } else if (applicant.status.userStatus == 'Candidate Submission') {
      if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'BDM Interview',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'Director' || this.currentUserRole == 'Manager' || this.currentUserRole == 'Advisor') {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'Manager Review',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'DSU Manager') {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
      else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      }
    } else if (applicant.status.userStatus == 'Manager Review') {
      if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'DSU Manager') {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: ['Introducer - Individual', 'Introducer - Corporate'].includes(applicant.designation) ? 'DSU Final Assessment' : applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      } else {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
    } else if (applicant.status.userStatus == 'Onboarding Processing') {
      if (this.currentUserRole == 'Director' || this.currentUserRole == 'Manager' || this.currentUserRole == 'Advisor') {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'Manager Review',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'DSU Manager') {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            designation: applicant.designation
          }
        });
      }
    } else if (['Rectification', 'Candidate Update (Due Diligence)', 'Candidate Update (Assessment)'].includes(applicant.status.userStatus)) {
      if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'DSU Manager') {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
      else {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
    } else if (applicant.status.userStatus == 'BDM Interview') {
      if (this.currentUserRole == 'Director' || this.currentUserRole == 'Manager') {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'Manager Review'

          }
        });
      } else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus
          }
        });
      } else if (this.currentUserRole == 'DSU Manager') {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
      else {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true
          }
        });
      }
    } else if (applicant.status.userStatus == 'DSU Initial Assessment' || applicant.status.userStatus == 'DSU Final Assessment') {
      if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'BDM Interview',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'Director' || this.currentUserRole == 'Manager' || this.currentUserRole == 'Advisor') {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'Manager Review',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'DSU Manager') {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
      else {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      }
    } else if (applicant.status.userStatus == 'DSU Manager Approval') {
      if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'BDM Interview',
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Director' || this.currentUserRole == 'Manager' || this.currentUserRole == 'Advisor') {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'Manager Review',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
      else {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      }
    } else if (applicant.status.userStatus == 'Onboarding Completed') {
      if (this.currentUserRole == 'BDM') {
        this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Director' || this.currentUserRole == 'Manager' || this.currentUserRole == 'Advisor') {
        this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: 'Manager Review',
            designation: applicant.designation

          }
        });
      } else if (this.currentUserRole == 'DSU') {
        this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      } else if (this.currentUserRole == 'Onboarding') {
        this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation

          }
        });
      }
      else {
        this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
          state: {
            id: applicant.id,
            isReadOnly: true,
            status: applicant.status.userStatus,
            designation: applicant.designation
          }
        });
      }
    }
  }

  editApplicant(applicant: AgentOnboarding): void {
    // alert(`Editing: ${applicant.name}`);
    if (applicant.status.userStatus == 'Draft Created') {
      this.router.navigate(['agent-onboarding/edit-onboarding-application'], {
        state: { id: applicant.id }
      });
    } else if (applicant.status.userStatus == 'Candidate Submission') {
      this.router.navigate(['agent-onboarding/edit-onboarding-application'], {
        state: { id: applicant.id }
      });
    } else if (applicant.status.userStatus == 'Manager Review' || applicant.status.userStatus == 'Rectification') {
      this.router.navigate(['agent-onboarding/rmnavigation-tab'], {
        state: {
          id: applicant.id,
          isReadOnly: false,
          status: applicant.status.userStatus,
          designation: applicant.designation

        }
      });

    } else if (applicant.status.userStatus == 'Onboarding Processing') {
      this.router.navigate(['agent-onboarding/onboardingnavigation-tab'], {
        state: {
          id: applicant.id,
          isReadOnly: false,
          iseditable: false,
          status: applicant.status.userStatus,
          designation: applicant.designation
        }
      });
    } else if (this.currentUserRole == 'BDM' && applicant.status.userStatus == 'BDM Interview') {
      this.router.navigate(['agent-onboarding/bdmnavigation-tab'], {
        state: {
          id: applicant.id,
          isReadOnly: false
        }
      });
    } else if (applicant.status.userStatus == 'DSU Initial Assessment' || applicant.status.userStatus == 'DSU Final Assessment') {
      this.router.navigate(['agent-onboarding/dsunavigation-tab'], {
        state: {
          id: applicant.id,
          isReadOnly: true,
          iseditable: true,
          status: applicant.status.userStatus,
          designation: applicant.designation
        }
      });
    } else if (applicant.status.userStatus == 'DSU Manager Approval') {
      this.router.navigate(['agent-onboarding/dsumanagernavigation-tab'], {
        state: {
          id: applicant.id,
          isReadOnly: true,
          iseditable: true,
          status: applicant.status.userStatus,
          designation: applicant.designation
        }
      });
    }
  }

  // deleteApplicant(applicant: Applicant): void {
  //   if (confirm(`Delete application from ${applicant.name}?`)) {
  //     this.allApplicants = this.allApplicants.filter(a => a.id !== applicant.id);
  //     this.__applyAgentonboardFilter();
  //   }
  // }

  /**
   * Opens the read-only onboarding timeline for one application. Available for
   * every row regardless of role — it exposes no application data beyond the
   * status transitions already visible in the table.
   */
  protected viewAuditLog(applicant: AgentOnboarding): void {
    const dialogData: OnboardingAuditLogDialogData = {
      id: applicant.id,
      applicantName: applicant.designation === 'Introducer - Corporate'
        ? applicant.companyName
        : applicant.fullNameNric,
      designation: applicant.designation,
      currentStatus: applicant.status.userStatus,
      dateOfApplication: applicant.dateOfApplication
    };

    this.matDialog.open(OnboardingAuditLogDialog, {
      width: '620px',
      maxWidth: '95vw',
      autoFocus: false,
      data: dialogData
    });
  }

  newApplication(): void {
    this.router.navigate(['/agent-onboarding/create-onboarding-application']);
  }

}
